import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, ShieldCheck, Key, Download, AlertTriangle } from 'lucide-react';
import { TwoFactorSetup } from '@/components/auth/TwoFactorSetup';
import { useTwoFactor } from '@/hooks/useTwoFactor';
import type { TwoFactorStatus } from '@/types/twoFactor';

interface TwoFactorSettingsProps {
  className?: string;
}

export const TwoFactorSettings: React.FC<TwoFactorSettingsProps> = ({ className }) => {
  const {
    status,
    isLoading,
    initiateSetup,
    disableTwoFactor,
    regenerateBackupCodes,
    setupData,
    isSetupLoading,
    backupCodes,
    isDisabling,
    isRegenerating,
    error,
    clearError,
  } = useTwoFactor();

  const [showSetup, setShowSetup] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const handleInitiateSetup = async () => {
    try {
      await initiateSetup();
      setShowSetup(true);
    } catch (err) {
      console.error('Failed to initiate 2FA setup:', err);
    }
  };

  const handleDisable2FA = async () => {
    if (window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      try {
        await disableTwoFactor();
      } catch (err) {
        console.error('Failed to disable 2FA:', err);
      }
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (window.confirm('Are you sure you want to regenerate backup codes? Your old backup codes will no longer work.')) {
      try {
        await regenerateBackupCodes();
        setShowBackupCodes(true);
      } catch (err) {
        console.error('Failed to regenerate backup codes:', err);
      }
    }
  };

  const handleSetupComplete = () => {
    setShowSetup(false);
    setShowBackupCodes(true);
  };

  const downloadBackupCodes = () => {
    if (!backupCodes || backupCodes.length === 0) return;

    const content = [
      'Two-Factor Authentication Backup Codes',
      '=======================================',
      '',
      'These codes can be used to access your account if you lose your authenticator device.',
      'Each code can only be used once. Keep them in a safe place.',
      '',
      ...backupCodes.map((code, index) => `${index + 1}. ${code}`),
      '',
      'Generated on: ' + new Date().toLocaleDateString('en-GB'),
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'hirelo-2fa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Two-Factor Authentication
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-4">
            <div className="animate-pulse">Loading...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {status?.is_enabled ? (
              <ShieldCheck className="h-5 w-5 text-green-600" />
            ) : (
              <Shield className="h-5 w-5 text-gray-400" />
            )}
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account by requiring a code from your phone when signing in.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
              <Button variant="outline" size="sm" onClick={clearError} className="ml-auto">
                Dismiss
              </Button>
            </Alert>
          )}

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">Status:</span>
                {status?.is_enabled ? (
                  <Badge variant="default" className="bg-green-100 text-green-800">
                    Enabled
                  </Badge>
                ) : (
                  <Badge variant="secondary">Disabled</Badge>
                )}
              </div>
              {status?.is_enabled && (
                <div className="text-sm text-muted-foreground">
                  Backup codes available: {status.backup_codes_count}
                  {status.last_used_timestamp && (
                    <div>
                      Last used: {new Date(status.last_used_timestamp).toLocaleDateString('en-GB')}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {!status?.is_enabled ? (
                <Button
                  onClick={handleInitiateSetup}
                  disabled={isSetupLoading}
                  data-testid="enable-2fa-button"
                >
                  {isSetupLoading ? 'Setting up...' : 'Enable 2FA'}
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateBackupCodes}
                    disabled={isRegenerating}
                    className="flex items-center gap-1"
                    data-testid="regenerate-backup-codes-button"
                  >
                    <Key className="h-4 w-4" />
                    {isRegenerating ? 'Generating...' : 'New Backup Codes'}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDisable2FA}
                    disabled={isDisabling}
                    data-testid="disable-2fa-button"
                  >
                    {isDisabling ? 'Disabling...' : 'Disable'}
                  </Button>
                </>
              )}
            </div>
          </div>

          {!status?.is_enabled && (
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="space-y-1">
                  <p className="font-medium">Why enable 2FA?</p>
                  <p className="text-sm text-muted-foreground">
                    Two-factor authentication adds an extra layer of security by requiring both your password 
                    and a code from your phone to sign in. This helps protect your account even if your password is compromised.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 2FA Setup Dialog */}
      <TwoFactorSetup
        isOpen={showSetup}
        onClose={() => setShowSetup(false)}
        onComplete={handleSetupComplete}
        setupData={setupData}
      />

      {/* Backup Codes Display Dialog */}
      {showBackupCodes && backupCodes && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Backup Codes
              </CardTitle>
              <CardDescription>
                Save these backup codes in a safe place. Each code can only be used once.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="p-2 bg-background rounded border">
                      {code}
                    </div>
                  ))}
                </div>
              </div>

              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Make sure to save these codes securely. You won't be able to see them again.
                </AlertDescription>
              </Alert>

              <div className="flex gap-2">
                <Button
                  onClick={downloadBackupCodes}
                  className="flex items-center gap-1"
                  data-testid="download-backup-codes-button"
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowBackupCodes(false)}
                  data-testid="close-backup-codes-button"
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};